import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, Box, Paper, TextField, Button, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    IconButton, Snackbar, Alert, useMediaQuery, useTheme, InputAdornment, Dialog, DialogContent,
    DialogActions, Grid, Chip, CircularProgress, Tooltip, Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';

const ORIGEM_LABEL = {
    ESPONTANEA: 'Espontanea', UBS: 'UBS', CAPS: 'CAPS',
    ESCOLA: 'Escola', JUDICIARIO: 'Judiciario', MEDICO: 'Medico',
};
const ALCOOL_LABEL = {
    NAO_USA: 'Nao usa', OCASIONAL: 'Ocasional', USO_RISCO: 'Uso de risco',
    ABUSO: 'Abuso', DEPENDENCIA: 'Dependencia',
};

const SectionCard = ({ icon, title, children, color = '#1ABB9C' }) => (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 1.2, bgcolor: `${color}15`, color }}>
                {icon}
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>{title}</Typography>
        </Box>
        {children}
    </Paper>
);

const InfoField = ({ label, value }) => (
    <Box>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', mb: 0.3 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1f2937', wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Box>
);

export default function Anamnese() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [lista, setLista] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selecionada, setSelecionada] = useState(null);

    const debounceRef = useRef(null);
    const mountedRef = useRef(false);

    const carregar = async (pg, rpp, busca) => {
        setLoading(true);
        try {
            const params = { page: pg, size: rpp };
            if (busca) params.busca = busca;
            const { data } = await api.get('/anamneses', { params });
            setLista(data.content ?? []);
            setTotalElements(data.totalElements ?? 0);
        } catch {
            mostrarMensagem('Erro ao carregar anamneses', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        carregar(0, rowsPerPage, '');
    }, []);

    useEffect(() => {
        if (!mountedRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            carregar(0, rowsPerPage, searchTerm);
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [searchTerm]);

    useEffect(() => {
        if (!mountedRef.current) return;
        carregar(page, rowsPerPage, searchTerm);
    }, [page, rowsPerPage]);

    const mostrarMensagem = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const formatarData = (iso) => iso ? new Date(iso).toLocaleString('pt-BR') : '—';
    const formatarDataCurta = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

    const handleDelete = async (id) => {
        const ok = await confirmAcao({
            title: 'Excluir anamnese?',
            text: 'A ficha inicial do paciente é perdida. Esta ação não pode ser desfeita.',
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/anamneses/${id}`, { skipGlobalError: true });
            toastSuccess('Anamnese excluída.');
            carregar(page, rowsPerPage, searchTerm);
        } catch (error) {
            mostrarErro(error, 'excluir a anamnese');
        }
    };

    const abrirView = async (id) => {
        try {
            const { data } = await api.get(`/anamneses/${id}`);
            setSelecionada(data);
            setViewModalOpen(true);
        } catch {
            mostrarMensagem('Erro ao carregar anamnese', 'error');
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2, mb: 3,
            }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                        Anamneses
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Total: {totalElements} {totalElements === 1 ? 'registro' : 'registros'}
                    </Typography>
                </Box>
                <Button
                    variant="contained" startIcon={<AddIcon />}
                    onClick={() => navigate('/anamnese/novo')}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 3 }}
                >
                    Nova Anamnese
                </Button>
            </Box>

            {/* Busca */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <TextField
                    fullWidth size="small" placeholder="Buscar por paciente ou queixa..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {/* Tabela */}
            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#fafafa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Paciente</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Queixa Principal</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Origem</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Preenchida</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Preenchido por</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="center">Versão</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} sx={{ color: '#1ABB9C' }} />
                                </TableCell></TableRow>
                            )}
                            {!loading && lista.length === 0 && (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#888' }}>
                                    Nenhuma anamnese encontrada.
                                </TableCell></TableRow>
                            )}
                            {!loading && lista.map(a => (
                                <TableRow key={a.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1ABB9C', fontSize: '0.85rem', fontWeight: 700 }}>
                                                {(a.nomePaciente || '?').charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{a.nomePaciente || '—'}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 260 }}>
                                        <Typography variant="body2" noWrap title={a.queixaEDemanda?.queixaPrincipal}>
                                            {a.queixaEDemanda?.queixaPrincipal || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {a.queixaEDemanda?.origemEncaminhamento
                                            ? <Chip size="small" label={ORIGEM_LABEL[a.queixaEDemanda.origemEncaminhamento] || a.queixaEDemanda.origemEncaminhamento} />
                                            : '—'}
                                    </TableCell>
                                    <TableCell>{formatarDataCurta(a.dataPreenchimento)}</TableCell>
                                    <TableCell>{a.preenchidoPor || '—'}</TableCell>
                                    <TableCell align="center"><Chip size="small" label={`v${a.versao ?? 0}`} variant="outlined" /></TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Visualizar"><IconButton size="small" onClick={() => abrirView(a.id)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Editar"><IconButton size="small" onClick={() => navigate(`/anamnese/${a.id}`)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Linhas por pagina:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>

            {/* View Modal */}
            <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
                {selecionada && (
                    <>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #1ABB9C 0%, #0e8a72 100%)',
                            px: { xs: 2.5, sm: 3.5 }, pt: 3, pb: 3,
                            display: 'flex', alignItems: 'flex-start', gap: 2,
                        }}>
                            <Avatar sx={{
                                width: { xs: 52, sm: 64 }, height: { xs: 52, sm: 64 },
                                bgcolor: 'rgba(255,255,255,0.18)', color: 'white',
                                fontSize: { xs: '1.4rem', sm: '1.8rem' }, fontWeight: 700,
                                border: '3px solid rgba(255,255,255,0.35)',
                            }}>
                                {(selecionada.nomePaciente || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} color="white" sx={{ lineHeight: 1.2, mb: 1 }}>
                                    {selecionada.nomePaciente}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    <Chip size="small" label={`Versao ${selecionada.versao ?? 0}`} sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontWeight: 600 }} />
                                    <Chip size="small" label={`Preenchida em ${formatarDataCurta(selecionada.dataPreenchimento)}`} sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.88)' }} />
                                    {selecionada.preenchidoPor && (
                                        <Chip size="small" label={selecionada.preenchidoPor} sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.88)' }} />
                                    )}
                                </Stack>
                            </Box>
                            <Tooltip title="Fechar">
                                <IconButton onClick={() => setViewModalOpen(false)} size="small"
                                    sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f4f6f8' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SectionCard icon={<ContentPasteIcon sx={{ fontSize: 19 }} />} title="Queixa e Demanda">
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 8 }}><InfoField label="Queixa Principal" value={selecionada.queixaEDemanda?.queixaPrincipal} /></Grid>
                                        <Grid size={{ xs: 12, sm: 4 }}><InfoField label="Origem" value={ORIGEM_LABEL[selecionada.queixaEDemanda?.origemEncaminhamento] || selecionada.queixaEDemanda?.origemEncaminhamento} /></Grid>
                                        <Grid size={12}><InfoField label="Historia da Demanda Atual" value={selecionada.queixaEDemanda?.historiaDemandaAtual} /></Grid>
                                    </Grid>
                                </SectionCard>

                                <SectionCard icon={<MedicalServicesOutlinedIcon sx={{ fontSize: 19 }} />} title="Historico Clinico" color="#6366f1">
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Diagnosticos Previos" value={selecionada.historicoClinico?.diagnosticosPrevios} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Historico de Doencas" value={selecionada.historicoClinico?.historicoDoencas} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Medicamentos em Uso" value={selecionada.historicoClinico?.medicamentosEmUso} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Alergias" value={selecionada.historicoClinico?.alergias} /></Grid>
                                        <Grid size={12}><InfoField label="Traumas/Acidentes" value={selecionada.historicoClinico?.historicoTraumasAcidentes ? (selecionada.historicoClinico?.descricaoTraumas || 'Sim') : 'Nao'} /></Grid>
                                    </Grid>
                                </SectionCard>

                                <SectionCard icon={<PsychologyOutlinedIcon sx={{ fontSize: 19 }} />} title="Historico Psiquiatrico" color="#f59e0b">
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Tratamento Anterior" value={selecionada.historicoPsiquiatrico?.tratamentoPsicologicoAnterior ? (selecionada.historicoPsiquiatrico?.detalheTratamentoAnterior || 'Sim') : 'Nao'} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Internacao Previa" value={selecionada.historicoPsiquiatrico?.internacaoPsiquiatricaPrevia ? 'Sim' : 'Nao'} /></Grid>
                                        <Grid size={12}><InfoField label="Historico Familiar" value={selecionada.historicoPsiquiatrico?.historicoPsiquiatricoFamiliar} /></Grid>
                                    </Grid>
                                </SectionCard>

                                <SectionCard icon={<LocalBarOutlinedIcon sx={{ fontSize: 19 }} />} title="Uso de Substancias" color="#ef4444">
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 6, sm: 4 }}><InfoField label="Tabaco" value={selecionada.usoDeSubstancias?.usoTabaco ? 'Sim' : 'Nao'} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4 }}><InfoField label="Outras Substancias" value={selecionada.usoDeSubstancias?.usoOutrasSubstancias ? 'Sim' : 'Nao'} /></Grid>
                                        <Grid size={{ xs: 12, sm: 4 }}><InfoField label="Alcool" value={ALCOOL_LABEL[selecionada.usoDeSubstancias?.usoAlcool] || selecionada.usoDeSubstancias?.usoAlcool} /></Grid>
                                        {selecionada.usoDeSubstancias?.usoOutrasSubstancias && (
                                            <Grid size={12}><InfoField label="Descricao" value={selecionada.usoDeSubstancias?.descricaoSubstancias} /></Grid>
                                        )}
                                    </Grid>
                                </SectionCard>

                                <SectionCard icon={<GroupsOutlinedIcon sx={{ fontSize: 19 }} />} title="Contexto Psicossocial" color="#0ea5e9">
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Situacao Familiar" value={selecionada.contextoPsicossocial?.situacaoFamiliar} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Situacao Ocupacional" value={selecionada.contextoPsicossocial?.situacaoOcupacional} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Suporte / Rede" value={selecionada.contextoPsicossocial?.suporteRedeSocial} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><InfoField label="Impacto na Rotina" value={selecionada.contextoPsicossocial?.impactoNaRotina} /></Grid>
                                    </Grid>
                                </SectionCard>

                                <SectionCard icon={<AssignmentOutlinedIcon sx={{ fontSize: 19 }} />} title="Plano Inicial" color="#8b5cf6">
                                    <Grid container spacing={2}>
                                        <Grid size={12}><InfoField label="Hipotese Inicial" value={selecionada.planoInicial?.hipoteseInicialDemanda} /></Grid>
                                        <Grid size={12}><InfoField label="Objetivos" value={selecionada.planoInicial?.objetivosIniciais} /></Grid>
                                        <Grid size={12}><InfoField label="Observacoes" value={selecionada.planoInicial?.observacoesGerais} /></Grid>
                                    </Grid>
                                </SectionCard>
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1px solid #eee', gap: 1 }}>
                            <Button onClick={() => setViewModalOpen(false)} variant="outlined" color="inherit">Fechar</Button>
                            <Button onClick={() => { setViewModalOpen(false); navigate(`/anamnese/${selecionada.id}`); }}
                                variant="contained" startIcon={<EditIcon />}
                                sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>
                                Editar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Confirm delete */}

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
